import React, { useContext, useEffect, useState } from 'react';
import {
	BellSnoozeIcon,
	PlusIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';

import {
	AttributeRow,
	TemplateBoolean,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
	TemplateTeamMembers,
} from '../../../components/Attributes/Attributes_old';
import useAsync from '../../../hooks/useAsync';
import { getTeam } from '../../../api/company';
import SettingsContext from '../../../context/SettingsContext';
import { getUserFullName } from '../../../components/helpers/functions';
import { AvatarGenerated } from '../../../components/Avatar';
import Button from '../../../components/Button';
import BetaAttributes from '../../../components/Attributes';
import { NOTIFY_OPTIONS } from '../../../constants';
import { classNames } from '../../../components/helpers/functions';
import useLocal from '../../../hooks/useLocal';
import useSettings from '../../../hooks/useSettings';
import Badge from '../../../components/Badge';
import Modal from '../../../components/Shell/Modal';
import usePrevious from '../../../hooks/usePrevious';
import { getAllPowerUsers } from '../../../api/power-users';

const AssignmentSettings = () => {
	const [notifyUserModal, setNotifyUserModal] = useState(false);

	const {
		get: getSettings,
		set: updateSettings,
		loading: isLoadingSettings,
	} = useSettings();

	const { value: teammembers, execute: onRefreshUsers } = useAsync(getTeam, {
		immediate: false,
	});

	const { value: availablePowerusers, execute: onRefreshAvailablePowerusers } =
		useAsync(getAllPowerUsers);

	const prevNotifyUserModal = usePrevious(notifyUserModal);

	useEffect(() => {
		if (!prevNotifyUserModal || (prevNotifyUserModal && !notifyUserModal)) {
			onRefreshAvailablePowerusers();
			onRefreshUsers();
		}
	}, [notifyUserModal]);

	const orgReminderContractsExpiringActive = getSettings(
		'orgReminderContractsExpiringActive'
	);

	const orgReminderContractsExpiredActive = getSettings(
		'orgReminderContractsExpiredActive'
	);

	const orgReminderContractsExpiringUsers = getSettings(
		'orgReminderContractsExpiringUsers'
	);

	const orgReminderContractsExpiredUsers = getSettings(
		'orgReminderContractsExpiredUsers'
	);

	/*
		Settings Constants

		orgReminderContractsExpiringActive: true | false 
		orgReminderContractsExpiringDays: 0 (Do not notify) | 3 | 7 | 14 | 30 | 60
		orgReminderContractsExpiringUsers: undefined | [] | [allAdmins, <userId>, <userId> ]

		orgReminderContractsExpiredActive: true | false
	*/

	return (
		<>
			<div>
				<h2 className="text-base font-semibold leading-6 text-gray-900">
					Contracts
				</h2>
				<dl className="mt-6 space-y-2 divide-y divide-gray-100 border-t border-gray-200 text-xs leading-6">
					<div className="pt-2 sm:flex">
						<dt className="font-medium text-gray-900 sm:w-96 sm:flex-none sm:pr-6 mt-2 text-xs text-gray-400 pt-2">
							<TemplateBoolean
								value={
									orgReminderContractsExpiringActive === null
										? true
										: orgReminderContractsExpiringActive
								}
								name="Contract expiring"
								description={'Send reminder before contract expires'}
								onChange={(index, value) =>
									updateSettings(
										'company',
										'orgReminderContractsExpiringActive',
										value
									)
								}
							/>
						</dt>
						<dd className="mt-1 flex justify-between gap-x-6 sm:flex-auto mt-2 mb-1 ">
							<div className="text-gray-900 space-y-3">
								<TemplateDropdown
									className="w-64"
									name="Number of days before expiration:"
									placeholder="Number of days before expiration"
									options={NOTIFY_OPTIONS}
									value={
										getSettings('orgReminderContractsExpiringDays') ||
										NOTIFY_OPTIONS.find((o) => o.value === 30)?.value
									}
									onChange={(index, value, raw) => {
										updateSettings(
											'company',
											'orgReminderContractsExpiringDays',
											value
										);
									}}
								/>

								<AttributeRow name="Notify users:">
									<div className="flex items-center flex-wrap">
										{!orgReminderContractsExpiringUsers ||
										orgReminderContractsExpiringUsers?.includes('allAdmins') ? (
											<Badge
												isBeta
												hasDot={false}
												border
												size="large"
												onClick={() =>
													updateSettings(
														'company',
														'orgReminderContractsExpiringUsers',
														orgReminderContractsExpiringUsers?.filter(
															(o) => o !== 'allAdmins'
														) || []
													)
												}
												ghost
												className="mr-1.5 mt-1.5"
											>
												All admins
												<XMarkIcon className="w-4 h-4 text-gray-400 ml-1.5" />
											</Badge>
										) : null}

										{orgReminderContractsExpiringUsers
											?.filter((o) => o !== 'allAdmins')
											.map((id) => {
												return (
													<Badge
														isBeta
														hasDot={false}
														border
														size="large"
														onClick={() =>
															updateSettings(
																'company',
																'orgReminderContractsExpiringUsers',
																orgReminderContractsExpiringUsers?.filter(
																	(o) => o !== id
																) || []
															)
														}
														ghost
														className="mr-1.5 mt-1.5"
													>
														{getUserFullName(
															[
																...(teammembers?.rows || []),
																...(availablePowerusers || []),
															].find((o) => o.id === id)
														)}
														<XMarkIcon className="w-4 h-4 text-gray-400 ml-1.5" />
													</Badge>
												);
											})}

										<Badge
											isBeta
											hasDot={false}
											border
											size="large"
											onClick={() =>
												setNotifyUserModal({
													disabled: orgReminderContractsExpiringUsers,
													settingIndex: 'orgReminderContractsExpiringUsers',
												})
											}
											ghost
											className="mr-1.5 mt-1.5"
										>
											<PlusIcon className="w-4 h-4 text-gray-400" />
										</Badge>
									</div>
								</AttributeRow>
							</div>
						</dd>
					</div>

					<div className="pt-2 sm:flex">
						<dt className="font-medium text-gray-900 sm:w-96 sm:flex-none sm:pr-6 flex  mt-2 text-xs text-gray-400 pt-2">
							<TemplateBoolean
								value={
									orgReminderContractsExpiredActive === null
										? true
										: orgReminderContractsExpiredActive
								}
								name="Contract expired"
								description={'Send reminder on the day a contract expires'}
								onChange={(index, value) =>
									updateSettings(
										'company',
										'orgReminderContractsExpiredActive',
										value
									)
								}
							/>
						</dt>
						<dd className="mt-1 flex justify-between gap-x-6 sm:flex-auto mt-2 mb-1 ">
							<div className="text-gray-900 space-y-3">
								<AttributeRow name="Notify users:">
									<div className="flex items-center flex-wrap">
										{!orgReminderContractsExpiredUsers ||
										orgReminderContractsExpiredUsers?.includes('allAdmins') ? (
											<Badge
												isBeta
												hasDot={false}
												border
												size="large"
												onClick={() =>
													updateSettings(
														'company',
														'orgReminderContractsExpiredUsers',
														orgReminderContractsExpiredUsers?.filter(
															(o) => o !== 'allAdmins'
														) || []
													)
												}
												ghost
												className="mr-1.5 mt-1.5"
											>
												All admins
												<XMarkIcon className="w-4 h-4 text-gray-400 ml-1.5" />
											</Badge>
										) : null}

										{orgReminderContractsExpiredUsers
											?.filter((o) => o !== 'allAdmins')
											.map((id) => {
												return (
													<Badge
														isBeta
														hasDot={false}
														border
														size="large"
														onClick={() =>
															updateSettings(
																'company',
																'orgReminderContractsExpiredUsers',
																orgReminderContractsExpiredUsers?.filter(
																	(o) => o !== id
																) || []
															)
														}
														ghost
														className="mr-1.5 mt-1.5"
													>
														{getUserFullName(
															[
																...(teammembers?.rows || []),
																...(availablePowerusers || []),
															].find((o) => o.id === id)
														)}
														<XMarkIcon className="w-4 h-4 text-gray-400 ml-1.5" />
													</Badge>
												);
											})}

										<Badge
											isBeta
											hasDot={false}
											border
											size="large"
											onClick={() =>
												setNotifyUserModal({
													disabled: orgReminderContractsExpiredUsers,
													settingIndex: 'orgReminderContractsExpiredUsers',
												})
											}
											ghost
											className="mr-1.5 mt-1.5"
										>
											<PlusIcon className="w-4 h-4 text-gray-400" />
										</Badge>
									</div>
								</AttributeRow>
							</div>
						</dd>
					</div>
				</dl>
			</div>

			<Modal
				title="Select a user to notify"
				open={notifyUserModal}
				onClose={() => setNotifyUserModal(false)}
			>
				<AttributeRow span="sm:col-span-1">
					<div className="flex items-center">
						<div className="w-full">
							<TemplateReactSelect
								placeholder="Select a user to notify"
								formatOptionLabel={({ label, type }) => (
									<div className="flex justify-between items-center cursor-pointer">
										<div>{label}</div>
										<div className="text-gray-400 text-xxs pl-3 truncate">
											{type === 'user'
												? 'User'
												: type === 'powermember'
												? 'Power User'
												: type}
										</div>
									</div>
								)}
								options={[
									{
										label: 'All admins',
										value: 'allAdmins',
										type: 'Default',
										disabled: notifyUserModal?.disabled?.includes('allAdmins'),
									},
									{
										label: 'Active Users',
										options: teammembers?.rows?.length
											? teammembers?.rows?.map((member) => ({
													label: getUserFullName(member),
													value: member?.id,
													type: 'user',
													disabled: notifyUserModal?.disabled?.includes(
														member?.id
													),
											  }))
											: [],
									},
									{
										label: 'Power Users',
										options: availablePowerusers?.length
											? availablePowerusers?.map((member) => ({
													label: getUserFullName(member),
													value: member?.id,
													type: 'powermember',
													disabled: notifyUserModal?.disabled?.includes(
														member?.id
													),
											  }))
											: [],
									},
								]}
								onChange={(index, user, raw) => {
									const value = Array.isArray(notifyUserModal?.disabled)
										? notifyUserModal?.disabled?.includes(user?.value)
											? notifyUserModal?.disabled
											: [...notifyUserModal?.disabled, user?.value]
										: ['allAdmins', user?.value];

									updateSettings(
										'company',
										notifyUserModal?.settingIndex,
										value
									);
									setNotifyUserModal(false);
								}}
							/>
						</div>
					</div>
				</AttributeRow>

				<div className="flex justify-end mt-3">
					<Button onClick={() => {}} disabled={true}>
						Add
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default AssignmentSettings;
